// src/components/reports/VaccineRequisitionChart.js
import React, { useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Box,
  Heading,
  HStack,
  FormControl,
  FormLabel,
  Select,
  Text,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartBar } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

const formatNumber = (value) => Number(value || 0).toLocaleString();

const VaccineRequisitionChart = ({ data, warehouseOptions }) => {
  const [selectedWarehouse, setSelectedWarehouse] = useState("");

  // จัดการการเปลี่ยนแปลงใน dropdown
  const handleWarehouseChange = (e) => {
    setSelectedWarehouse(e.target.value);
  };

  // กรองคลังที่มีข้อมูลใน data (ใช้ ToWarehouseName)
  const availableWarehouses = [
    ...new Set(data.map((item) => item.ToWarehouseName)),
  ].filter(Boolean); // กรองค่าที่ไม่ใช่ null/undefined
  const filteredWarehouseOptions = warehouseOptions.filter(
    (warehouse) =>
      warehouse.WarehouseName &&
      availableWarehouses.includes(warehouse.WarehouseName)
  );

  // เตรียมข้อมูลสำหรับกราฟ
  const chartData = () => {
    if (!selectedWarehouse) {
      // รวมยอดทุกคลังตาม VaccineNameTH
      const groupedData = data.reduce((acc, item) => {
        const vaccineName = item.VaccineNameTH;
        const packageSize = Number(item.PackageSize) || 0;
        const requestQuantity = Number(item.RequestQuantity) || 0;
        const approvedQuantity = Number(item.ApprovedQuantity) || 0;
        const requestDoses =
          item.RequestDoses !== undefined && item.RequestDoses !== null
            ? Number(item.RequestDoses) || 0
            : requestQuantity * packageSize;
        const approvedDoses =
          item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
            ? Number(item.ApprovedDoses) || 0
            : approvedQuantity * packageSize;
        if (!acc[vaccineName]) {
          acc[vaccineName] = {
            name: vaccineName,
            requestQuantity: 0,
            approvedQuantity: 0,
            requestDoses: 0,
            approvedDoses: 0,
            vaccineEN: item.VaccineNameEN,
            warehouse: "ทุกคลัง",
          };
        }
        acc[vaccineName].requestQuantity += requestQuantity;
        acc[vaccineName].approvedQuantity += approvedQuantity;
        acc[vaccineName].requestDoses += requestDoses;
        acc[vaccineName].approvedDoses += approvedDoses;
        return acc;
      }, {});

      return Object.values(groupedData);
    } else {
      // กรองข้อมูลตามคลังที่เลือก (ToWarehouseName)
      return data
        .filter((item) => item.ToWarehouseName === selectedWarehouse)
        .map((item) => {
          const packageSize = Number(item.PackageSize) || 0;
          const requestQuantity = Number(item.RequestQuantity) || 0;
          const approvedQuantity = Number(item.ApprovedQuantity) || 0;
          const requestDoses =
            item.RequestDoses !== undefined && item.RequestDoses !== null
              ? Number(item.RequestDoses) || 0
              : requestQuantity * packageSize;
          const approvedDoses =
            item.ApprovedDoses !== undefined && item.ApprovedDoses !== null
              ? Number(item.ApprovedDoses) || 0
              : approvedQuantity * packageSize;

          return {
            name: item.VaccineNameTH,
            requestQuantity,
            approvedQuantity,
            requestDoses,
            approvedDoses,
            vaccineEN: item.VaccineNameEN,
            warehouse: item.ToWarehouseName,
          };
        });
    }
  };

  // กำหนดสีสำหรับกราฟ
  const requestBarColor = "#8884d8"; // Purple
  const approvedBarColor = "#82ca9d"; // Green

  // Tooltip ที่กำหนดเอง
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box
          bg="white"
          p={3}
          border="1px solid #ccc"
          borderRadius="md"
          boxShadow="md"
        >
          <p>
            <strong>ชื่อวัคซีน (TH):</strong> {label}
          </p>
          <p>
            <strong>ชื่อวัคซีน (EN):</strong> {data.vaccineEN}
          </p>
          <p>
            <strong>ชื่อคลัง:</strong> {data.warehouse}
          </p>
          <p>
            <strong>จำนวนขอเบิก:</strong>{" "}
            {formatNumber(data.requestQuantity)} ขวด /{" "}
            {formatNumber(data.requestDoses)} โดส
          </p>
          <p>
            <strong>จำนวนอนุมัติ:</strong>{" "}
            {formatNumber(data.approvedQuantity)} ขวด /{" "}
            {formatNumber(data.approvedDoses)} โดส
          </p>
        </Box>
      );
    }
    return null;
  };

  return (
    <Box
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
      p={5}
      mt={6}
      bg="white"
    >
      <StandardHeader
        title="กราฟยอดการขอเบิกวัคซีน"
        bgColor="iGreen.500"
        icon={faChartBar}
      />
      <FormControl m={5} maxW="300px">
        <FormLabel fontSize="xl" color="gray">
          เลือกคลัง
        </FormLabel>
        {filteredWarehouseOptions.length > 0 ? (
          <Select
            value={selectedWarehouse}
            onChange={handleWarehouseChange}
            placeholder="ทุกคลัง"
            colorScheme="iBlue"
          >
            {filteredWarehouseOptions.map((warehouse) => (
              <option
                key={warehouse.WarehouseID}
                value={warehouse.WarehouseName}
              >
                {warehouse.WarehouseName}
              </option>
            ))}
          </Select>
        ) : (
          <Text color="gray.500">ไม่มีคลังที่มีข้อมูล</Text>
        )}
      </FormControl>
      {chartData().length > 0 ? (
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData()}
            margin={{ top: 40, right: 30, left: 20, bottom: 60 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              interval={0}
              height={150}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              label={{
                value: "จำนวน (หน่วย)",
                angle: -90,
                position: "insideLeft",
                offset: -5,
              }}
              tick={{ fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              layout="horizontal"
              verticalAlign="top"
              align="right"
              wrapperStyle={{ paddingBottom: 10 }}
            />
            <Bar
              dataKey="requestQuantity"
              name="ยอดขอเบิก (ขวด)"
              fill={requestBarColor}
              stackId="a"
              barSize={25}
            />
            <Bar
              dataKey="approvedQuantity"
              name="ยอดอนุมัติ (ขวด)"
              fill={approvedBarColor}
              stackId="a"
              barSize={25}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Text textAlign="center" color="gray.500" p={5}>
          ไม่มีข้อมูลสำหรับแสดงกราฟ
        </Text>
      )}
    </Box>
  );
};

export default VaccineRequisitionChart;